import {ApolloLink, Observable} from '@apollo/client';
import {getMainDefinition} from '@apollo/client/utilities';
import type {
  InternalOperation,
  unstable_InternalOperation as RevampInternalOperation,
} from '@sail/data';
import type {OperationDefinitionNode} from 'graphql';

import {
  getInteropAccountId,
  getInteropAccountIdPromise,
} from '../../interopAccountId';
import type {InteropFields} from './RestLink/rewriteInteropParams';

function hasInteropField(
  interopFields: InteropFields,
  operation: InternalOperation | RevampInternalOperation,
): boolean {
  const definition = getMainDefinition(
    operation.query,
  ) as OperationDefinitionNode;
  const opType = definition.operation;

  return definition.selectionSet.selections.some((selection) => {
    if (selection.kind === 'Field') {
      return Boolean(interopFields[opType]?.[selection.name.value]);
    }
    return false;
  });
}

export default function createWaitForInteropAccountLink(
  interopFields: InteropFields,
) {
  return new ApolloLink(function waitForInteropAccountLink(
    operation: InternalOperation | RevampInternalOperation,
    forward,
  ) {
    if (!hasInteropField(interopFields, operation)) {
      return forward(operation);
    }

    const v1Context = operation.variables.v1Context as
      | {id?: string}
      | undefined;
    if (!v1Context?.id?.startsWith('org_')) {
      return forward(operation);
    }

    if (getInteropAccountId() !== null) {
      return forward(operation);
    }

    const interopAccountIdPromise = getInteropAccountIdPromise();

    if (interopAccountIdPromise === null) {
      throw new Error(
        `Missing interop account id promise for operation ${operation.operationName}`,
      );
    }

    return new Observable((observer) => {
      let isUnsubscribed = false;
      let subscription: {unsubscribe: () => void} | null = null;

      interopAccountIdPromise
        .then(() => {
          if (isUnsubscribed) {
            return;
          }

          subscription = forward(operation).subscribe({
            next: (result) => observer.next(result),
            error: (error) => observer.error(error),
            complete: () => observer.complete(),
          });
        })
        .catch((error) => {
          if (!isUnsubscribed) {
            observer.error(error);
          }
        });

      return () => {
        isUnsubscribed = true;
        subscription?.unsubscribe();
      };
    });
  });
}
